import { useState, useEffect } from "react";
import axios from "axios";
import {
  Users, Briefcase, UserCog, Plus, Trash2,
  CheckCircle2, Star, Sparkles, RefreshCw, Shield, Flame,
  UserPlus, Eye, EyeOff, Mail, Download, Send, X, Clock, MessageSquare, Gift,
  LayoutDashboard, TrendingUp, AlertTriangle, Award, Handshake
} from "lucide-react";
import { CreateIntroductionModal } from "../../components/CreateIntroductionModal";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "profiles", label: "Membres", icon: Users },
  { id: "opportunities", label: "Opportunités", icon: Briefcase },
  { id: "benefits", label: "Avantages", icon: Gift },
  { id: "reviews", label: "Avis", icon: MessageSquare },
  { id: "introductions", label: "Mises en relation", icon: Handshake },
  { id: "security", label: "Sécurité", icon: Shield },
  { id: "users", label: "Utilisateurs", icon: UserCog },
];

const TYPE_LABELS = { cherche: "Je cherche", propose: "Je propose", opportunite: "Opportunité" };

function BadgeToggle({ active, onToggle, label, testId }) {
  return (
    <button data-testid={testId} onClick={onToggle}
      className={`cbm-badge transition-all cursor-pointer ${active ? "cbm-badge-featured border-gold" : "cbm-badge-closed opacity-50"}`}>
      {label}
    </button>
  );
}

// ── Dashboard tab ─────────────────────────────────────────────────────────────
function KpiCell({ icon: Icon, label, value, accent = "#C9A227", testId }) {
  return (
    <div className="cbm-card p-4" data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: `${accent}22`, border: `1px solid ${accent}55` }}
        >
          <Icon size={13} style={{ color: accent }} />
        </div>
        <p className="cbm-label">{label}</p>
      </div>
      <p className="font-outfit font-bold text-white" style={{ fontSize: "1.75rem", lineHeight: 1, letterSpacing: "-0.02em" }}>
        {value}
      </p>
    </div>
  );
}

function DashboardTab({ onGoTab }) {
  const [data, setData] = useState(null);
  const [securityAlerts, setSecurityAlerts] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/admin/dashboard`, { withCredentials: true }),
      axios.get(`${API}/api/admin/security/alerts/count`, { withCredentials: true }),
    ])
      .then(([dashRes, secRes]) => {
        setData(dashRes.data);
        setSecurityAlerts(secRes.data.count || 0);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-cbm-muted text-sm p-6">Chargement...</div>;
  if (!data) return <div className="text-cbm-muted text-sm p-6">Erreur de chargement</div>;

  const k = data.kpis;
  const TYPE_BADGES_ADMIN = {
    cherche: "cbm-badge-type-cherche",
    propose: "cbm-badge-type-propose",
    opportunite: "cbm-badge-type-opportunite",
  };

  return (
    <div className="p-5 space-y-5" data-testid="admin-dashboard">
      {/* KPI Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCell icon={Users} label="Membres total" value={k.total_profiles} testId="kpi-total-profiles" />
        <KpiCell icon={UserPlus} label="Nouveaux (7j)" value={k.new_members_week} accent="#22c55e" testId="kpi-new-members" />
        <KpiCell icon={AlertTriangle} label="À valider" value={k.needs_review} accent="#f97316" testId="kpi-needs-review" />
        <KpiCell icon={Briefcase} label="Opportunités actives" value={k.open_opps} testId="kpi-open-opps" />
        <KpiCell icon={CheckCircle2} label="Opportunités clôturées" value={k.closed_opps} accent="#94a3b8" testId="kpi-closed-opps" />
        <KpiCell icon={TrendingUp} label="Taux de résolution" value={`${k.resolution_rate}%`} accent="#22c55e" testId="kpi-resolution-rate" />
      </div>

      {/* Alerts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          data-testid="alert-needs-review"
          onClick={() => onGoTab("profiles", { filter: "review" })}
          className="cbm-card p-4 text-left flex items-center gap-3 hover:border-gold/50 transition-all"
          style={{ borderLeftWidth: "3px", borderLeftColor: "#f97316" }}
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(249,115,22,0.15)" }}>
            <AlertTriangle size={16} style={{ color: "#f97316" }} />
          </div>
          <div className="flex-1">
            <p className="text-white text-sm font-semibold">{data.alerts.needs_review_count} profil{data.alerts.needs_review_count > 1 ? "s" : ""} à valider</p>
            <p className="text-cbm-muted text-xs">Fonction "Autre" en attente de revue admin</p>
          </div>
        </button>
        <button
          data-testid="alert-inactive"
          onClick={() => onGoTab("profiles", { filter: "inactive" })}
          className="cbm-card p-4 text-left flex items-center gap-3 hover:border-gold/50 transition-all"
          style={{ borderLeftWidth: "3px", borderLeftColor: "#94a3b8" }}
        >
          <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(148,163,184,0.15)" }}>
            <Clock size={16} style={{ color: "#B8C4E0" }} />
          </div>
          <div className="flex-1">
            <p className="text-white text-sm font-semibold">{data.alerts.inactive_count} membre{data.alerts.inactive_count > 1 ? "s" : ""} inactif{data.alerts.inactive_count > 1 ? "s" : ""}</p>
            <p className="text-cbm-muted text-xs">Aucune activité depuis 30 jours</p>
          </div>
        </button>
        {securityAlerts > 0 && (
          <button
            data-testid="alert-security"
            onClick={() => onGoTab("security")}
            className="cbm-card p-4 text-left flex items-center gap-3 hover:border-gold/50 transition-all"
            style={{ borderLeftWidth: "3px", borderLeftColor: "#ef4444" }}
          >
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "rgba(239,68,68,0.15)" }}>
              <Shield size={16} style={{ color: "#ef4444" }} />
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-semibold">{securityAlerts} activité{securityAlerts > 1 ? "s" : ""} suspecte{securityAlerts > 1 ? "s" : ""}</p>
              <p className="text-cbm-muted text-xs">Comportement anormal détecté</p>
            </div>
          </button>
        )}
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="cbm-card p-4" data-testid="recent-members">
          <p className="cbm-label mb-3">Derniers membres inscrits</p>
          {data.recent_members.length === 0 ? (
            <p className="text-cbm-muted text-sm">Aucun</p>
          ) : (
            <ul className="space-y-2">
              {data.recent_members.map(m => (
                <li key={m._id} className="text-sm flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-medium truncate">{m.nom_prenom}</p>
                    <p className="text-cbm-muted text-xs truncate">{m.fonction || m.secteur || "—"}</p>
                  </div>
                  <span className="text-cbm-muted text-xs flex-shrink-0">
                    {m.created_at ? new Date(m.created_at).toLocaleDateString("fr-FR") : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="cbm-card p-4" data-testid="recent-opps">
          <p className="cbm-label mb-3">Dernières opportunités</p>
          {data.recent_opportunities.length === 0 ? (
            <p className="text-cbm-muted text-sm">Aucune</p>
          ) : (
            <ul className="space-y-2">
              {data.recent_opportunities.map(o => (
                <li key={o._id} className="text-sm">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`cbm-badge ${TYPE_BADGES_ADMIN[o.type] || ""}`}>{TYPE_LABELS[o.type] || o.type}</span>
                    <span className="text-white font-medium truncate">{o.titre}</span>
                  </div>
                  <p className="text-cbm-muted text-xs mt-0.5">{o.secteur} · {o.statut === "ouvert" ? "ouvert" : "clôturé"}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Invite Modal ─────────────────────────────────────────────────────────────
function InviteModal({ onClose, onSuccess }) {
  const [form, setForm] = useState({ email: "", name: "" });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/admin/invite`, form, { withCredentials: true });
      setResult(data);
      // Modal stays open to show the invite link to admin
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md mx-4 cbm-glass rounded-2xl p-6 shadow-gold-strong animate-fade-in-up">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-outfit font-semibold text-lg flex items-center gap-2" style={{ color: '#FFFFFF', fontWeight: 600 }}>
            <UserPlus size={20} className="text-gold" />
            Inviter un membre
          </h3>
          <button onClick={onClose} className="text-cbm-muted hover:text-white"><X size={18} /></button>
        </div>
        {result ? (
          <div className="text-center">
            <CheckCircle2 size={36} className="text-green-400 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Invitation envoyée !</p>
            <p className="text-cbm-secondary text-sm mb-3">{result.message}</p>
            <div className="bg-navy rounded-lg p-3 mb-4">
              <p className="cbm-label mb-1">Lien d'invitation (copie de secours)</p>
              <p className="text-gold text-xs break-all font-mono">{result.link}</p>
            </div>
            <button className="cbm-btn-primary text-sm" onClick={onClose}>Fermer</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="cbm-label block mb-1.5">Prénom & Nom *</label>
              <input data-testid="invite-name" className="cbm-input" placeholder="Prénom Nom" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
            </div>
            <div>
              <label className="cbm-label block mb-1.5">Email *</label>
              <input data-testid="invite-email" type="email" className="cbm-input" placeholder="email@exemple.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
            </div>
            {error && <p className="text-red-400 text-sm bg-red-900/20 rounded-lg px-3 py-2">{error}</p>}
            <div className="flex gap-3 pt-1">
              <button type="button" className="cbm-btn-ghost text-sm flex-1 border border-white/10" onClick={onClose}>Annuler</button>
              <button data-testid="send-invite-submit" type="submit" disabled={sending} className="cbm-btn-primary text-sm flex-1 justify-center">
                {sending ? <><span className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" />Envoi...</> : <><Send size={14} /> Envoyer l'invitation</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Profiles tab ──────────────────────────────────────────────────────────────
function ProfilesTab({ onExport, initialFilter = "all" }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);

  useEffect(() => { setFilter(initialFilter); }, [initialFilter]);

  useEffect(() => {
    axios.get(`${API}/api/admin/profiles`, { withCredentials: true }).then(({ data }) => setProfiles(data)).finally(() => setLoading(false));
  }, []);

  const toggle = async (pid, field) => {
    const p = profiles.find(x => x._id === pid);
    const newVal = !p[field];
    await axios.put(`${API}/api/admin/profiles/${pid}`, { [field]: newVal }, { withCredentials: true });
    setProfiles(prev => prev.map(x => x._id === pid ? { ...x, [field]: newVal } : x));
  };

  const companyVerify = async (pid, action) => {
    const updated = await axios.post(
      `${API}/api/admin/profiles/${pid}/company-verification`,
      { action },
      { withCredentials: true }
    );
    setProfiles(prev => prev.map(x => x._id === pid ? { ...x, ...updated.data } : x));
  };

  const deleteProfile = async (pid) => {
    if (!window.confirm("Supprimer ce profil ?")) return;
    await axios.delete(`${API}/api/admin/profiles/${pid}`, { withCredentials: true });
    setProfiles(prev => prev.filter(x => x._id !== pid));
  };

  const approveReview = async (pid) => {
    await axios.put(`${API}/api/admin/profiles/${pid}`, { needs_review: false }, { withCredentials: true });
    setProfiles(prev => prev.map(x => x._id === pid ? { ...x, needs_review: false } : x));
  };

  const rejectReview = async (pid) => {
    if (!window.confirm("Refuser et supprimer ce profil ?")) return;
    await axios.delete(`${API}/api/admin/profiles/${pid}`, { withCredentials: true });
    setProfiles(prev => prev.filter(x => x._id !== pid));
  };

  if (loading) return <div className="text-cbm-muted text-sm p-6">Chargement...</div>;

  const reviewCount = profiles.filter(p => p.needs_review).length;
  const inactiveCount = profiles.filter(p => !p.active_recent).length;

  // Sort : needs_review first → active_recent → inactive
  const sorted = [...profiles].sort((a, b) => {
    const rank = (p) => p.needs_review ? 0 : p.active_recent ? 1 : 2;
    return rank(a) - rank(b);
  });

  let filtered = sorted;
  if (filter === "review") filtered = sorted.filter(p => p.needs_review);
  else if (filter === "inactive") filtered = sorted.filter(p => !p.active_recent);

  return (
    <div>
      <div className="flex items-center justify-between px-4 py-3 border-b cbm-divider flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="profiles-filter-all"
            onClick={() => setFilter("all")}
            className={`cbm-badge cursor-pointer transition-all ${filter === "all" ? "cbm-badge-verified opacity-100" : "cbm-badge-closed opacity-60"}`}
          >
            Tous ({profiles.length})
          </button>
          <button
            data-testid="profiles-filter-review"
            onClick={() => setFilter("review")}
            className={`cbm-badge cursor-pointer transition-all ${filter === "review" ? "cbm-badge-new opacity-100" : "cbm-badge-closed opacity-60"}`}
          >
            À valider ({reviewCount})
          </button>
          <button
            data-testid="profiles-filter-inactive"
            onClick={() => setFilter("inactive")}
            className={`cbm-badge cursor-pointer transition-all ${filter === "inactive" ? "cbm-badge-closed border-white/30 opacity-100" : "cbm-badge-closed opacity-60"}`}
          >
            Inactifs ({inactiveCount})
          </button>
        </div>
        <button data-testid="export-csv-button" className="cbm-btn-secondary text-xs" onClick={onExport}>
          <Download size={13} /> Export CSV
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b cbm-divider">
              {["Membre", "Secteur / Ville", "Complétion", "Activité", "Badges", "Entreprise", "Annonces", "Actions"].map(h => <th key={h} className="cbm-label text-left px-3 py-2.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => {
              const pct = p.completion_pct || 0;
              const pctColor = pct >= 75 ? "#22c55e" : pct >= 40 ? "#C9A227" : "#f97316";
              return (
                <tr key={p._id} className="border-b cbm-divider hover:bg-white/2 transition-colors" data-testid={`admin-profile-${p._id}`}>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-white text-sm">{p.nom_prenom}</span>
                      {p.needs_review && (
                        <span
                          className="cbm-badge cbm-badge-new"
                          data-testid={`badge-needs-review-${p._id}`}
                          title={`Fonction "Autre" — à valider`}
                        >
                          À valider
                        </span>
                      )}
                    </div>
                    <div className="text-cbm-muted text-xs">{p.fonction || p.entreprise || ""}</div>
                  </td>
                  <td className="px-3 py-3 text-cbm-secondary text-xs"><div>{p.secteur}</div><div>{p.ville}</div></td>
                  <td className="px-3 py-3" data-testid={`completion-${p._id}`}>
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(184,196,224,0.12)" }}>
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pctColor }} />
                      </div>
                      <span className="text-xs font-semibold" style={{ color: pctColor }}>{pct}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3" data-testid={`activity-${p._id}`}>
                    {p.active_recent ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color: "#22c55e" }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#22c55e", boxShadow: "0 0 6px rgba(34,197,94,0.6)" }} />
                        Actif récemment
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-cbm-muted">
                        <span className="w-1.5 h-1.5 rounded-full bg-cbm-muted opacity-60" />
                        Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <BadgeToggle active={p.is_featured} onToggle={() => toggle(p._id, "is_featured")} label="★ Une" testId={`toggle-featured-${p._id}`} />
                      <BadgeToggle active={p.is_verified} onToggle={() => toggle(p._id, "is_verified")} label="✓ Vérifié" testId={`toggle-verified-${p._id}`} />
                      <BadgeToggle active={p.is_recommended} onToggle={() => toggle(p._id, "is_recommended")} label="✦ Reco" testId={`toggle-recommended-${p._id}`} />
                      <BadgeToggle active={p.is_new} onToggle={() => toggle(p._id, "is_new")} label="New" testId={`toggle-new-${p._id}`} />
                      <BadgeToggle active={p.is_ambassador} onToggle={() => toggle(p._id, "is_ambassador")} label="◆ Ambassadeur" testId={`toggle-ambassador-${p._id}`} />
                      <BadgeToggle active={p.is_top_connector} onToggle={() => toggle(p._id, "is_top_connector")} label="⚡ Top connecteur" testId={`toggle-top-connector-${p._id}`} />
                    </div>
                  </td>
                  <td className="px-3 py-3 text-cbm-muted text-xs text-center"><span className="text-gold font-bold">{p.annonces_utilisees}</span>/{p.annonces_max}</td>
                  <td className="px-3 py-3" data-testid={`company-verif-${p._id}`}>
                    {(() => {
                      const status = p.company_verification_status || "none";
                      if (p.is_company_verified || status === "verified") {
                        return (
                          <span className="cbm-badge cbm-badge-open inline-flex items-center gap-1" title="Entreprise vérifiée">
                            <CheckCircle2 size={10} /> Vérifiée
                          </span>
                        );
                      }
                      if (status === "pending") {
                        return (
                          <div className="flex flex-col gap-1.5">
                            <span className="cbm-badge cbm-badge-new" title={`ICE/Patente : ${p.ice_patente || "—"}`}>
                              À vérifier
                            </span>
                            <span className="text-cbm-muted text-[11px] font-mono break-all">{p.ice_patente || "—"}</span>
                            <div className="flex items-center gap-1">
                              <button
                                data-testid={`company-approve-${p._id}`}
                                onClick={() => companyVerify(p._id, "approve")}
                                className="cbm-btn-ghost text-[11px] py-0.5 px-1.5 border"
                                style={{ borderColor: "rgba(34,197,94,0.45)", color: "#22c55e" }}
                              >
                                Valider
                              </button>
                              <button
                                data-testid={`company-reject-${p._id}`}
                                onClick={() => companyVerify(p._id, "reject")}
                                className="cbm-btn-ghost text-[11px] py-0.5 px-1.5 border"
                                style={{ borderColor: "rgba(239,68,68,0.4)", color: "#f87171" }}
                              >
                                Refuser
                              </button>
                            </div>
                          </div>
                        );
                      }
                      if (status === "rejected") {
                        return <span className="cbm-badge cbm-badge-closed" title="Vérification refusée">Refusée</span>;
                      }
                      return <span className="text-cbm-muted text-xs">—</span>;
                    })()}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {p.needs_review && (
                        <>
                          <button
                            data-testid={`approve-review-${p._id}`}
                            onClick={() => approveReview(p._id)}
                            className="cbm-btn-ghost text-xs py-1 px-2 border"
                            style={{ borderColor: "rgba(34,197,94,0.45)", color: "#22c55e" }}
                            title="Valider le profil"
                          >
                            Valider
                          </button>
                          <button
                            data-testid={`reject-review-${p._id}`}
                            onClick={() => rejectReview(p._id)}
                            className="cbm-btn-ghost text-xs py-1 px-2 border"
                            style={{ borderColor: "rgba(239,68,68,0.4)", color: "#f87171" }}
                            title="Refuser (supprime le profil)"
                          >
                            Refuser
                          </button>
                        </>
                      )}
                      <button data-testid={`delete-profile-${p._id}`} className="cbm-btn-ghost text-red-400 hover:text-red-300 hover:bg-red-900/10 text-xs p-1.5" onClick={() => deleteProfile(p._id)}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-cbm-muted text-sm p-4 text-center">Aucun profil</p>}
      </div>
    </div>
  );
}

// ── Opportunities tab ─────────────────────────────────────────────────────────
function OpportunitiesTab() {
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/opportunities`, { withCredentials: true }).then(({ data }) => setOpps(data)).finally(() => setLoading(false));
  }, []);

  const toggleField = async (id, field, current) => {
    await axios.put(`${API}/api/admin/opportunities/${id}`, { [field]: !current }, { withCredentials: true });
    setOpps(prev => prev.map(o => o._id === id ? { ...o, [field]: !current } : o));
  };
  const toggleStatus = async (opp) => {
    const ns = opp.statut === "ouvert" ? "cloture" : "ouvert";
    await axios.put(`${API}/api/admin/opportunities/${opp._id}`, { statut: ns }, { withCredentials: true });
    setOpps(prev => prev.map(o => o._id === opp._id ? { ...o, statut: ns } : o));
  };
  const deleteOpp = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    await axios.delete(`${API}/api/admin/opportunities/${id}`, { withCredentials: true });
    setOpps(prev => prev.filter(o => o._id !== id));
  };

  if (loading) return <div className="text-cbm-muted text-sm p-6">Chargement...</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b cbm-divider">
            {["Annonce", "Type / Auteur", "Statut", "Engagement", "Badges", "Actions"].map(h => <th key={h} className="cbm-label text-left px-3 py-2.5">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {opps.map(o => (
            <tr key={o._id} className="border-b cbm-divider hover:bg-white/2 transition-colors" data-testid={`admin-opp-${o._id}`}>
              <td className="px-3 py-3 max-w-xs"><div className="font-medium text-white text-sm line-clamp-1">{o.titre}</div><div className="text-cbm-muted text-xs">{o.secteur} · {o.ville}</div></td>
              <td className="px-3 py-3">
                <span className={`cbm-badge ${o.type === "cherche" ? "cbm-badge-type-cherche" : o.type === "propose" ? "cbm-badge-type-propose" : "cbm-badge-type-opportunite"} mb-1`}>{TYPE_LABELS[o.type] || o.type}</span>
                {o.author && <div className="text-cbm-muted text-xs mt-1">{o.author.nom_prenom}</div>}
              </td>
              <td className="px-3 py-3">
                <button data-testid={`toggle-status-${o._id}`} className={`cbm-badge cursor-pointer ${o.statut === "ouvert" ? "cbm-badge-open" : "cbm-badge-closed"}`} onClick={() => toggleStatus(o)}>
                  {o.statut === "ouvert" ? "Ouvert" : "Clôturé"}
                </button>
              </td>
              <td className="px-3 py-3 text-cbm-muted text-xs" data-testid={`opp-engagement-${o._id}`}>
                {(o.views_count || o.clicks_count) ? (
                  <div className="flex items-center gap-2">
                    <Eye size={12} className="text-gold" />
                    <span className="text-white font-medium">{o.views_count || 0}</span>
                  </div>
                ) : (
                  <span className="text-cbm-muted">—</span>
                )}
              </td>
              <td className="px-3 py-3">
                <div className="flex gap-1.5 flex-wrap">
                  <BadgeToggle active={o.is_featured} onToggle={() => toggleField(o._id, "is_featured", o.is_featured)} label="★ Une" testId={`toggle-opp-featured-${o._id}`} />
                  <BadgeToggle active={o.is_priority} onToggle={() => toggleField(o._id, "is_priority", o.is_priority)} label="! Priorité" testId={`toggle-opp-priority-${o._id}`} />
                </div>
              </td>
              <td className="px-3 py-3">
                <button data-testid={`delete-opp-admin-${o._id}`} className="cbm-btn-ghost text-red-400 hover:text-red-300 hover:bg-red-900/10 text-xs p-1.5" onClick={() => deleteOpp(o._id)}>
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {opps.length === 0 && <p className="text-cbm-muted text-sm p-4 text-center">Aucune annonce</p>}
    </div>
  );
}

// ── Benefits tab ──────────────────────────────────────────────────────────────
const BENEFIT_TYPE_LABELS = { service: "Service", produit: "Produit", outil: "Outil", autre: "Autre" };

function BenefitsTab() {
  const [benefits, setBenefits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/benefits`, { withCredentials: true });
      setBenefits(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await axios.patch(`${API}/api/admin/benefits/${id}`, { status }, { withCredentials: true });
    setBenefits(prev => prev.map(b => b._id === id ? { ...b, status } : b));
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avantage définitivement ?")) return;
    await axios.delete(`${API}/api/admin/benefits/${id}`, { withCredentials: true });
    setBenefits(prev => prev.filter(b => b._id !== id));
  };

  const filtered = benefits.filter(b => filter === "all" ? true : b.status === filter);
  const counts = {
    pending: benefits.filter(b => b.status === "pending").length,
    approved: benefits.filter(b => b.status === "approved").length,
    rejected: benefits.filter(b => b.status === "rejected").length,
  };

  if (loading) return <div className="text-cbm-muted text-sm p-6">Chargement...</div>;

  const filters = [
    { id: "pending", label: `En attente (${counts.pending})`, color: "cbm-badge-new" },
    { id: "approved", label: `Approuvés (${counts.approved})`, color: "cbm-badge-open" },
    { id: "rejected", label: `Refusés (${counts.rejected})`, color: "cbm-badge-closed" },
    { id: "all", label: `Tous (${benefits.length})`, color: "cbm-badge-verified" },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-3 border-b cbm-divider flex-wrap">
        {filters.map(f => (
          <button
            key={f.id}
            data-testid={`benefits-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`cbm-badge cursor-pointer transition-all ${filter === f.id ? f.color + " opacity-100" : "cbm-badge-closed opacity-60"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-cbm-muted text-sm p-6 text-center">Aucun avantage dans cette catégorie</p>
      ) : (
        <div className="p-4 space-y-3">
          {filtered.map(b => (
            <div key={b._id} className="cbm-card p-4" data-testid={`admin-benefit-${b._id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <span
                      className="inline-block px-3 py-1 rounded-md font-outfit font-bold text-base"
                      style={{
                        background: "linear-gradient(135deg, rgba(201,162,39,0.2), rgba(201,162,39,0.08))",
                        border: "1px solid rgba(201,162,39,0.35)",
                        color: "#DDC168",
                      }}
                    >
                      {b.value}
                    </span>
                    <span className="cbm-badge cbm-badge-verified">{BENEFIT_TYPE_LABELS[b.type] || b.type}</span>
                    <span className={`cbm-badge ${b.status === "approved" ? "cbm-badge-open" : b.status === "rejected" ? "cbm-badge-closed" : "cbm-badge-new"}`}>
                      {b.status === "approved" ? "Approuvé" : b.status === "rejected" ? "Refusé" : "En attente"}
                    </span>
                    <span className="text-cbm-muted text-xs">{new Date(b.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                  <h4 className="font-outfit font-semibold text-white text-sm mb-1">{b.title}</h4>
                  <p className="text-cbm-secondary text-sm mb-2 whitespace-pre-line">{b.description}</p>
                  {b.conditions && <p className="text-cbm-muted text-xs italic border-l-2 border-gold/30 pl-2 mb-2">{b.conditions}</p>}
                  <p className="text-cbm-muted text-xs">
                    Proposé par : <span className="text-white">{b.author?.nom_prenom || b.author_name}</span>
                    {b.author?.entreprise && <span className="text-cbm-muted"> · {b.author.entreprise}</span>}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  {b.status !== "approved" && (
                    <button data-testid={`approve-benefit-${b._id}`} className="cbm-btn-primary text-xs py-1.5 px-3" onClick={() => updateStatus(b._id, "approved")}>
                      <CheckCircle2 size={12} /> Approuver
                    </button>
                  )}
                  {b.status !== "rejected" && (
                    <button data-testid={`reject-benefit-${b._id}`} className="cbm-btn-ghost text-xs py-1.5 px-3 border border-red-800/30 text-red-400 hover:bg-red-900/10" onClick={() => updateStatus(b._id, "rejected")}>
                      <X size={12} /> Refuser
                    </button>
                  )}
                  <button data-testid={`delete-benefit-${b._id}`} className="cbm-btn-ghost text-xs py-1 px-2 text-red-400 hover:text-red-300 hover:bg-red-900/10" onClick={() => remove(b._id)}>
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Reviews tab ───────────────────────────────────────────────────────────────
function ReviewsTab() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/reviews`, { withCredentials: true });
      setReviews(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet avis définitivement ?")) return;
    await axios.delete(`${API}/api/admin/reviews/${id}`, { withCredentials: true });
    setReviews(prev => prev.filter(r => r._id !== id));
  };

  const renderStars = (n) => "★".repeat(n) + "☆".repeat(5 - n);

  const filtered = reviews.filter(r => filter === "all" ? true : r.status === filter);
  const counts = {
    pending: reviews.filter(r => r.status === "pending").length,
    approved: reviews.filter(r => r.status === "approved").length,
    rejected: reviews.filter(r => r.status === "rejected").length,
  };

  if (loading) return <div className="text-cbm-muted text-sm p-6">Chargement...</div>;

  const filters = [
    { id: "pending", label: `En attente (${counts.pending})`, color: "cbm-badge-new" },
    { id: "approved", label: `Approuvés (${counts.approved})`, color: "cbm-badge-open" },
    { id: "rejected", label: `Refusés (${counts.rejected})`, color: "cbm-badge-closed" },
    { id: "all", label: `Tous (${reviews.length})`, color: "cbm-badge-verified" },
  ];

  return (
    <div>
      <div className="flex items-center gap-2 px-4 py-3 border-b cbm-divider flex-wrap">
        {filters.map(f => (
          <button
            key={f.id}
            data-testid={`reviews-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`cbm-badge cursor-pointer transition-all ${filter === f.id ? f.color + " opacity-100" : "cbm-badge-closed opacity-60"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <p className="text-cbm-muted text-sm p-6 text-center">Aucun avis dans cette catégorie</p>
      ) : (
        <div className="p-4 space-y-3">
          {filtered.map(r => (
            <div key={r._id} className="cbm-card p-4" data-testid={`admin-review-${r._id}`}>
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-gold text-sm tracking-wider" title={`${r.rating}/5`}>{renderStars(r.rating)}</span>
                    <span className="text-white text-sm font-medium">{r.rating}/5</span>
                    {r.rating >= 4.5 && (
                      <span
                        className="cbm-badge inline-flex items-center gap-1"
                        style={{ background: "rgba(201,162,39,0.2)", border: "1px solid rgba(201,162,39,0.55)", color: "#C9A227" }}
                        data-testid={`top-partner-${r._id}`}
                        title="Note ≥ 4.5 — Top partenaire"
                      >
                        <Award size={10} /> Top partenaire
                      </span>
                    )}
                    <span className={`cbm-badge ${r.status === "approved" ? "cbm-badge-open" : r.status === "rejected" ? "cbm-badge-closed" : "cbm-badge-new"}`}>
                      {r.status === "approved" ? "Approuvé" : r.status === "rejected" ? "Refusé" : "En attente"}
                    </span>
                    <span className="text-cbm-muted text-xs">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                  </div>
                  <p className="text-cbm-secondary text-sm mb-2 whitespace-pre-line">{r.comment}</p>
                  <div className="text-cbm-muted text-xs flex items-center gap-3 flex-wrap">
                    <span><span className="text-cbm-muted">De :</span> <span className="text-white">{r.author?.nom_prenom || r.author_name || "Membre"}</span></span>
                    <span>→</span>
                    <span><span className="text-cbm-muted">Pour :</span> <span className="text-white">{r.target?.nom_prenom || "—"}</span>{r.target?.entreprise && <span className="text-cbm-muted"> · {r.target.entreprise}</span>}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-shrink-0">
                  <button data-testid={`delete-review-${r._id}`} className="cbm-btn-ghost text-xs py-1.5 px-3 text-red-400 hover:text-red-300 hover:bg-red-900/10 border border-red-800/30" onClick={() => remove(r._id)}>
                    <Trash2 size={12} /> Supprimer
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Security tab ──────────────────────────────────────────────────────────────
function SecurityTab() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  const loadAlerts = () => {
    axios.get(`${API}/api/admin/security/alerts`, { withCredentials: true })
      .then(({ data }) => setAlerts(data.alerts || []))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadAlerts(); }, []);

  const handleResolve = async (alertId, action) => {
    if (!window.confirm(`Confirmer : ${action === "mark_ok" ? "Marquer comme OK" : "Désactiver ce compte"} ?`)) return;
    
    setProcessing(alertId);
    try {
      await axios.post(
        `${API}/api/admin/security/alerts/${alertId}/resolve`,
        { action },
        { withCredentials: true }
      );
      loadAlerts(); // Refresh list
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur");
    } finally {
      setProcessing(null);
    }
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      "profile_views": "Consultation de profils",
      "directory_pages": "Navigation annuaire",
      "opportunity_view": "Consultation d'opportunités",
    };
    return labels[type] || type;
  };

  if (loading) return <div className="p-6 text-cbm-muted text-sm">Chargement...</div>;

  return (
    <div className="p-5 space-y-4" data-testid="security-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-outfit font-semibold text-white text-lg">Alertes de sécurité</h2>
        <span className="cbm-badge cbm-badge-new">{alerts.length} alerte{alerts.length > 1 ? "s" : ""}</span>
      </div>

      {alerts.length === 0 ? (
        <div className="cbm-card p-6 text-center">
          <Shield size={32} className="text-green-400 mx-auto mb-2" />
          <p className="text-white text-sm font-medium">Aucune activité suspecte</p>
          <p className="text-cbm-muted text-xs mt-1">Tout va bien !</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(alert => (
            <div key={alert._id} className="cbm-card p-4" data-testid={`security-alert-${alert._id}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Shield size={14} className="text-red-400" />
                    <span className="text-white text-sm font-semibold">{getAlertTypeLabel(alert.alert_type)}</span>
                  </div>
                  <p className="text-cbm-secondary text-sm">
                    <span className="text-gold font-medium">{alert.actions_count} actions</span> en {" "}
                    {alert.period_start && alert.period_end && (
                      <>
                        {Math.round((new Date(alert.period_end) - new Date(alert.period_start)) / 60000)} min
                      </>
                    )}
                  </p>
                </div>
                <span className="cbm-badge cbm-badge-closed text-xs">
                  {alert.created_at ? new Date(alert.created_at).toLocaleString("fr-FR", { 
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" 
                  }) : "—"}
                </span>
              </div>

              {/* User info */}
              <div className="bg-navy-darker rounded-lg p-3 mb-3">
                <p className="text-cbm-label text-xs mb-1">Utilisateur concerné</p>
                {alert.user_info || alert.profile_info ? (
                  <>
                    {alert.profile_info && (
                      <p className="text-white text-sm font-medium">{alert.profile_info.nom_prenom}</p>
                    )}
                    {alert.profile_info?.entreprise && (
                      <p className="text-cbm-muted text-xs">{alert.profile_info.entreprise}</p>
                    )}
                    {alert.user_info && (
                      <p className="text-cbm-secondary text-xs mt-1">{alert.user_info.email}</p>
                    )}
                  </>
                ) : (
                  <p className="text-cbm-muted text-xs">ID: {alert.user_id}</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleResolve(alert._id, "mark_ok")}
                  disabled={processing === alert._id}
                  className="cbm-btn-secondary text-xs flex-1"
                  data-testid={`resolve-ok-${alert._id}`}
                >
                  <CheckCircle2 size={12} /> Marquer OK
                </button>
                <button
                  onClick={() => handleResolve(alert._id, "disable_account")}
                  disabled={processing === alert._id}
                  className="text-xs flex-1"
                  style={{
                    padding: "0.5rem 0.75rem",
                    background: "rgba(239,68,68,0.1)",
                    border: "1px solid rgba(239,68,68,0.3)",
                    color: "#ef4444",
                    borderRadius: "0.5rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.25rem",
                  }}
                  data-testid={`disable-account-${alert._id}`}
                >
                  <X size={12} /> Désactiver compte
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────
function UsersTab({ onInvite }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "user" });
  const [creating, setCreating] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [formError, setFormError] = useState("");

  const load = () => {
    axios.get(`${API}/api/admin/users`, { withCredentials: true }).then(({ data }) => setUsers(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const createUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setFormError("");
    try {
      const { data } = await axios.post(`${API}/api/admin/users`, form, { withCredentials: true });
      setUsers(prev => [data, ...prev]);
      setShowForm(false);
      setForm({ email: "", password: "", name: "", role: "user" });
    } catch (err) {
      setFormError(err.response?.data?.detail || "Erreur");
    } finally {
      setCreating(false);
    }
  };

  const updateStatus = async (uid, status) => {
    await axios.put(`${API}/api/admin/users/${uid}/status`, { status }, { withCredentials: true });
    setUsers(prev => prev.map(u => u._id === uid ? { ...u, status } : u));
  };

  const deleteUser = async (uid) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    await axios.delete(`${API}/api/admin/users/${uid}`, { withCredentials: true });
    setUsers(prev => prev.filter(u => u._id !== uid));
  };

  if (loading) return <div className="text-cbm-muted text-sm p-6">Chargement...</div>;

  const pendingUsers = users.filter(u => u.status === "pending");

  return (
    <div>
      {/* Actions bar */}
      <div className="flex items-center justify-between px-4 py-3 border-b cbm-divider flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {pendingUsers.length > 0 && (
            <span className="cbm-badge cbm-badge-new animate-pulse">
              <Clock size={10} className="mr-0.5" />{pendingUsers.length} en attente
            </span>
          )}
          <p className="text-cbm-muted text-xs">{users.length} utilisateurs</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="invite-member-button" className="cbm-btn-primary text-xs" onClick={onInvite}>
            <Mail size={13} /> Inviter un membre
          </button>
          <button data-testid="create-user-toggle" className="cbm-btn-secondary text-xs" onClick={() => setShowForm(!showForm)}>
            <UserPlus size={13} /> {showForm ? "Annuler" : "Créer directement"}
          </button>
        </div>
      </div>

      {/* Pending users highlight */}
      {pendingUsers.length > 0 && (
        <div className="mx-4 my-3 cbm-card p-4 border-l-2 border-gold animate-fade-in">
          <p className="cbm-label mb-2 text-gold">En attente d'approbation</p>
          <div className="space-y-2">
            {pendingUsers.map(u => (
              <div key={u._id} className="flex items-center justify-between gap-3" data-testid={`pending-user-${u._id}`}>
                <div>
                  <p className="text-white text-sm font-medium">{u.name || u.email}</p>
                  <p className="text-cbm-muted text-xs">{u.email}</p>
                </div>
                <div className="flex gap-2">
                  <button data-testid={`approve-user-${u._id}`} className="cbm-btn-primary text-xs py-1.5 px-3" onClick={() => updateStatus(u._id, "active")}>
                    <CheckCircle2 size={13} /> Approuver
                  </button>
                  <button data-testid={`reject-user-${u._id}`} className="cbm-btn-ghost text-xs py-1.5 px-3 border border-red-800/30 text-red-400 hover:bg-red-900/10" onClick={() => updateStatus(u._id, "rejected")}>
                    <X size={13} /> Refuser
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create user form */}
      {showForm && (
        <form onSubmit={createUser} className="mx-4 my-3 cbm-card p-5 animate-fade-in">
          <h3 className="font-outfit font-semibold text-white text-sm mb-4">Créer un compte directement</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="cbm-label block mb-1">Nom *</label><input data-testid="new-user-name" className="cbm-input" placeholder="Nom complet" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required /></div>
            <div><label className="cbm-label block mb-1">Email *</label><input data-testid="new-user-email" type="email" className="cbm-input" placeholder="email@exemple.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></div>
            <div className="relative"><label className="cbm-label block mb-1">Mot de passe *</label>
              <input data-testid="new-user-password" type={showPwd ? "text" : "password"} className="cbm-input pr-10" placeholder="••••••••" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
              <button type="button" className="absolute right-3 bottom-2.5 text-cbm-muted" onClick={() => setShowPwd(!showPwd)}>{showPwd ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            </div>
            <div><label className="cbm-label block mb-1">Rôle</label>
              <select data-testid="new-user-role" className="cbm-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                <option value="user">Membre</option><option value="admin">Admin</option>
              </select>
            </div>
          </div>
          {formError && <p className="text-red-400 text-xs mt-2">{formError}</p>}
          <button data-testid="submit-create-user" type="submit" disabled={creating} className="cbm-btn-primary text-sm mt-4">{creating ? "Création..." : "Créer le compte"}</button>
        </form>
      )}

      {/* Users table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b cbm-divider">
              {["Utilisateur", "Rôle", "Statut", "Créé le", "Actions"].map(h => <th key={h} className="cbm-label text-left px-3 py-2.5">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u._id} className="border-b cbm-divider hover:bg-white/2 transition-colors" data-testid={`admin-user-${u._id}`}>
                <td className="px-3 py-3"><div className="font-medium text-white text-sm">{u.name || "—"}</div><div className="text-cbm-muted text-xs">{u.email}</div></td>
                <td className="px-3 py-3"><span className={`cbm-badge ${u.role === "admin" ? "cbm-badge-featured" : "cbm-badge-verified"}`}>{u.role === "admin" ? "Admin" : "Membre"}</span></td>
                <td className="px-3 py-3">
                  <span className={`cbm-badge ${u.status === "active" ? "cbm-badge-open" : u.status === "pending" ? "cbm-badge-new" : "cbm-badge-closed"}`}>
                    {u.status === "active" ? "Actif" : u.status === "pending" ? "En attente" : "Refusé"}
                  </span>
                </td>
                <td className="px-3 py-3 text-cbm-muted text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "—"}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1.5">
                    {u.status === "pending" && <button data-testid={`approve-user-row-${u._id}`} className="cbm-badge cbm-badge-open cursor-pointer hover:opacity-80" onClick={() => updateStatus(u._id, "active")}>Approuver</button>}
                    {u.status === "active" && u.role !== "admin" && <button data-testid={`deactivate-user-${u._id}`} className="cbm-badge cbm-badge-closed cursor-pointer hover:opacity-80" onClick={() => updateStatus(u._id, "rejected")}>Désactiver</button>}
                    {u.role !== "admin" && <button data-testid={`delete-user-${u._id}`} className="cbm-btn-ghost text-red-400 hover:text-red-300 hover:bg-red-900/10 text-xs p-1.5" onClick={() => deleteUser(u._id)}><Trash2 size={14} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Admin Panel ──────────────────────────────────────────────────────────
function IntroductionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/admin/introductions`, { withCredentials: true });
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  const STATUS_BADGES = {
    active: { label: "Active", cls: "cbm-badge-new" },
    connected: { label: "Connectés", cls: "cbm-badge-verified" },
    ignored: { label: "Ignorée", cls: "cbm-badge-closed" },
  };

  return (
    <div className="p-5">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h2 className="cbm-section-title">Mises en relation</h2>
          <p className="text-cbm-muted text-xs mt-0.5">Recommandations admin entre membres</p>
        </div>
        <button
          data-testid="create-introduction-button"
          onClick={() => setShowCreate(true)}
          className="cbm-btn-primary text-sm"
        >
          <Plus size={15} /> Créer une recommandation
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { id: "all", label: `Toutes (${items.length})` },
          { id: "active", label: `Actives (${items.filter(i => i.status === "active").length})` },
          { id: "connected", label: `Connectées (${items.filter(i => i.status === "connected").length})` },
          { id: "ignored", label: `Ignorées (${items.filter(i => i.status === "ignored").length})` },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`introductions-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`cbm-badge cursor-pointer transition-all ${filter === f.id ? "cbm-badge-featured opacity-100" : "cbm-badge-closed opacity-60"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-cbm-muted text-sm py-6">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10">
          <Handshake size={28} className="text-cbm-muted mx-auto mb-2 opacity-60" />
          <p className="text-cbm-secondary text-sm">Aucune recommandation à afficher</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((i) => {
            const badge = STATUS_BADGES[i.status] || STATUS_BADGES.active;
            return (
              <div key={i._id} className="cbm-card p-4" data-testid={`admin-introduction-${i._id}`}>
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-white font-semibold">{i.user_a?.nom_prenom}</span>
                    <span className="text-cbm-muted">↔</span>
                    <span className="text-white font-semibold">{i.user_b?.nom_prenom}</span>
                  </div>
                  <span className={`cbm-badge ${badge.cls} text-xs`}>{badge.label}</span>
                </div>
                <p className="text-cbm-muted text-xs mb-2">
                  {i.user_a?.entreprise || "—"} · {i.user_b?.entreprise || "—"}
                </p>
                <p className="text-cbm-secondary text-sm leading-relaxed">{i.message}</p>
                <p className="text-cbm-muted text-xs mt-2 flex items-center gap-1">
                  <Clock size={11} />
                  {new Date(i.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                </p>
              </div>
            );
          })}
        </div>
      )}

      <CreateIntroductionModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={() => load()}
      />
    </div>
  );
}


export default function AdminPanel() {
  const [tab, setTab] = useState("dashboard");
  const [profilesFilter, setProfilesFilter] = useState("all");
  const [showInvite, setShowInvite] = useState(false);

  const goTab = (id, opts = {}) => {
    if (id === "profiles" && opts.filter) {
      setProfilesFilter(opts.filter);
    }
    setTab(id);
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/profiles/export`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "membres_clubbusiness_maroc.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert("Erreur lors de l'export CSV");
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto animate-fade-in">
      {showInvite && <InviteModal onClose={() => setShowInvite(false)} onSuccess={() => { setShowInvite(false); setTab("users"); }} />}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(201,162,39,0.15)", border: "1px solid rgba(201,162,39,0.3)" }}>
            <Shield size={16} className="text-gold" />
          </div>
          <div>
            <h1 className="cbm-section-title leading-tight">Administration</h1>
            <p className="text-cbm-muted text-xs mt-0.5">Pilotage du club</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b cbm-divider pb-0 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium font-inter transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === id ? "border-gold text-gold" : "border-transparent text-cbm-muted hover:text-cbm-secondary"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      <div className={tab === "dashboard" ? "animate-fade-in" : "cbm-card p-0 overflow-hidden animate-fade-in"}>
        {tab === "dashboard" && <DashboardTab onGoTab={goTab} />}
        {tab === "profiles" && <ProfilesTab onExport={handleExportCSV} initialFilter={profilesFilter} />}
        {tab === "opportunities" && <OpportunitiesTab />}
        {tab === "benefits" && <BenefitsTab />}
        {tab === "reviews" && <ReviewsTab />}
        {tab === "introductions" && <IntroductionsTab />}
        {tab === "security" && <SecurityTab />}
        {tab === "users" && <UsersTab onInvite={() => setShowInvite(true)} />}
      </div>
    </div>
  );
}
